import { add, compareAsc } from 'date-fns';
import { ChangeEvent, useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';

import { CalendarIcon, LeftAngleIcon, RightAngleIcon } from '../../../../assets/svg';
import { ETPayButton } from '../../../../components/button';
import { Checkbox } from '../../../../components/form/checkbox';
import { Input } from '../../../../components/input';
import { ModalLinks } from '../../../../components/modal-links';
import { Spinner } from '../../../../components/spinner';
import { Switch } from '../../../../components/switch';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import { ResponsePLCustom } from '../../../../models/payment-links-custom';
import {
	AmountType,
	CustomField,
	ParamsNewLink,
	PaymentTypes,
} from '../../../../models/response-new-link';
import { PaymentLinksServices } from '../../../../services/payment-links';
import { getMonthAndYearFormat, HoursMinutes } from '../../../../utils/dates';
import { errorFormatter } from '../../../../utils/errors';
import { RangeDate } from '../../../payment-links-beta/components/new-single-payment';
import { CircleStepOne, CircleStepTwo } from './components/icons';
import { PLCardLink } from './components/pl-card';
import { QRTabPL } from './components/qr-tab';
import {
	ChangeMonthContainer,
	CurrentMonthYear,
	DividerLink,
	HeaderMonthYearSelected,
	ShowDatePickerContainer,
} from './components/styles';
import {
	BodyStep,
	CardContainer,
	CardField,
	CardForm,
	ContainerAmount,
	ContainerDates,
	ContainerForm,
	ContainerSelectNative,
	ContainerTextAndName,
	CustomDataLink,
	DataLink,
	Disclaimer,
	DividerForm,
	DividerTabs,
	HeaderContainer,
	HeaderStep,
	InputContainer,
	ModalContainer,
	ModalFooter,
	ModalFooterQr,
	ParagraphGlosa,
	SelectNavite,
	TitleStep,
	WarningText,
} from './styles';
import { generatefield } from './util';

interface NewSinglePaymentProps {
	show: boolean;
	onClose: () => void;
}

const optionsMock = [
	{
		value: 'VARIABLE',
		label: 'VARIABLE',
		show: true,
	},
	{
		value: 'FIXED',
		label: 'FIJO',
		show: true,
	},
];

interface StatesPage extends ParamsNewLink {
	loading: boolean;
}

const alphanumericWithSpacesRegex = /^[a-zA-Z0-9\s]*$/;
const currentValue = 'Ahora';
const d = new Date();

const NewLinkPL = ({ show, onClose }: NewSinglePaymentProps) => {
	const { merchantSelected } = useMerchant();
	const { handleShowAlert } = useFloatingAlert();
	const [typeAmount, setTypeAmoun] = useState<AmountType>('VARIABLE');
	const [dinamicFields, setDinamicFields] = useState<CustomField[]>([]);
	const [plCardSelected, setPlCardSelected] = useState<PaymentTypes>('RECURRENT');

	const [range, setRange] = useState<RangeDate>(() => ({
		from: d,
		to: add(d, { days: 1 }),
	}));

	const [showCalendar, setShowCalendar] = useState({
		from: false,
		to: false,
	});

	const [response, setResponse] = useState<ResponsePLCustom>({
		pl_custom_url: null,
		pl_custom_qr: null,
	});

	const defaultValues: StatesPage = {
		has_end_expiration_date: false,
		amount_type: typeAmount,
		end_expired_at: null,
		fields: [],
		init_expired_at: range.from,
		loading: false,
		merchant_code: merchantSelected?.code || '',
		name: '',
		num_payments: plCardSelected === 'UNIQUE' ? 1 : 0,
		order_amount_max: null,
		order_amount_min: null,
		order_amount: null,
		payment_type: plCardSelected,
	};

	const [amountsQR, setAmountsQR] = useState('');
	const [state, setState] = useState<StatesPage>(defaultValues);
	const [withExpirationDate, setWithExpirationDate] = useState(false);

	const handleWithExpirationDate = () => {
		const newWithExpirationDate = !withExpirationDate;
		setWithExpirationDate(newWithExpirationDate);

		if (newWithExpirationDate) {
			const dateTo = add(state.init_expired_at, { days: 1 });
			dateTo.setHours(12, 0, 0, 0);
			setRange({
				from: state.init_expired_at,
				to: dateTo,
			});
			setState({
				...state,
				end_expired_at: dateTo,
				has_end_expiration_date: newWithExpirationDate,
			});
		} else {
			setState({
				...state,
				end_expired_at: null,
				has_end_expiration_date: newWithExpirationDate,
			});
		}
	};

	const handleToggleRequired = (key: string) => {
		if (!key) {
			console.error('[🔥] not key');
			return;
		}

		const fields = dinamicFields.map((f) => {
			if (f.key === key) {
				f.required = !f.required;
			}
			return f;
		});

		setDinamicFields(fields);
	};
	const [checkedRequiredFields, setCheckedRequiredFields] = useState<{ [key: string]: boolean }>(
		{},
	);

	const handleToggleCustomField = (key: string) => {
		if (!key) {
			console.error('[🔥] not key');
			return;
		}

		let fields: CustomField[] = dinamicFields;
		let customField: CustomField;
		const existfield = fields.some((f) => f.key === key);

		if (existfield) {
			fields = fields.filter((f) => f.key !== key);
			setCheckedRequiredFields((prev) => ({
				...prev,
				[key]: false,
			}));
		} else {
			customField = generatefield(key);
			fields = [...fields, customField];
		}
		setDinamicFields(fields);
	};

	const handleCloseModal = () => {
		setState(defaultValues);
		onClose();
	};

	const handleAddPL = async () => {
		try {
			const { loading, ...rest } = state;

			rest.fields = dinamicFields;
			if (typeAmount === 'FIXED') {
				const removeCharacter = rest.order_amount?.toString().replace('.', '');
				const orderAmount = Number(removeCharacter);

				rest.order_amount = orderAmount;
			}

			if (typeAmount === 'VARIABLE') {
				const removeCharacterMinAmount = rest.order_amount_min?.toString().replace('.', '');
				const removeCharacterMaxAmount = rest.order_amount_max?.toString().replace('.', '');
				const orderMinAmount = Number(removeCharacterMinAmount);
				const orderMaxAmount = Number(removeCharacterMaxAmount);

				rest.order_amount = null;
				rest.order_amount_min = orderMinAmount;
				rest.order_amount_max = orderMaxAmount;
			}

			if (!state.has_end_expiration_date) {
				setState({ ...state, end_expired_at: null });
				rest.end_expired_at = null;
			}

			setState({ ...state, loading: !loading });

			const { pl_custom_qr, pl_custom_url } = await PaymentLinksServices.addNewPaymentLink(rest);
			setAmountsQR(handleEvalueateAmountQr(state));
			setResponse({
				pl_custom_qr: pl_custom_qr,
				pl_custom_url: pl_custom_url,
			});
		} catch (e: any) {
			handleShowAlert({
				message: errorFormatter(e.code),
				type: 'error',
			});
		} finally {
			setState(defaultValues);
		}
	};

	const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		let dinamicValue: any = value;

		if (name === 'num_payments') {
			dinamicValue = Number(value);
		}
		if (typeAmount === 'FIXED' && name === 'order_amount') {
			setState({
				...state,
				order_amount_min: null,
				order_amount_max: null,
			});
		}

		if (typeAmount === 'VARIABLE' && name === 'order_amount') {
			setState({
				...state,
				order_amount: null,
			});
		}
		setState({ ...state, [name]: dinamicValue });
	};

	const onChangeDate = (selectedDate: Date, property: 'from' | 'to') => {
		if (property === 'from') {
			setRange({
				from: selectedDate,
				to: selectedDate,
			});
			setState((prev) => ({
				...prev,
				init_expired_at: selectedDate,
				end_expired_at: null,
			}));

			const newHours = hoursMinutesStart.filter((h) => h.value !== currentValue);
			setHoursMinutesStart(newHours);
		} else {
			if (!selectedDate) return;

			setRange({
				...range,
				to: selectedDate,
			});
			setState((prev) => ({
				...prev,
				end_expired_at: selectedDate,
			}));
		}
		setShowCalendar({ ...showCalendar, [property]: false });
	};

	const handleKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === '.') {
			e.preventDefault(); // Evita que el punto se escriba
			return;
		} else if (!alphanumericWithSpacesRegex.test(e.key)) {
			e.preventDefault(); // Evita que se escriban caracteres no permitidos
		}
	};

	const validateFields = (state: StatesPage): boolean => {
		if (!state.name || !state.merchant_code) {
			return false;
		}

		// Amount validation based on type
		if (state.amount_type === 'FIXED') {
			if (!state.order_amount) return false;
		} else {
			if (!state.order_amount_min || !state.order_amount_max) return false;
			if (state.order_amount_min > state.order_amount_max) return false;
		}

		// Validate dates
		if (!state.init_expired_at) return false;

		// If expiration date is set, validate it
		if (state.end_expired_at) {
			if (compareAsc(state.end_expired_at, state.init_expired_at) === -1) return false;
			// if (state.end_expired_at <= state.init_expired_at) return false;
		}

		// For recurrent payments, validate num_payments
		if (state.payment_type === 'RECURRENT' && typeof state.num_payments === 'object') {
			return false;
		}

		return true;
	};

	useEffect(() => {
		validateFields(state);
	}, [state]);

	const handleEvalueateAmountQr = (stateAfterResponse: StatesPage): string => {
		let amount = '';

		if (typeAmount === 'FIXED' && stateAfterResponse.order_amount) {
			amount = `${formatNumber(stateAfterResponse.order_amount.toString())}`;
		} else if (stateAfterResponse.order_amount_min || stateAfterResponse.order_amount_max) {
			amount = `${formatNumber(
				stateAfterResponse.order_amount_min?.toString() || '0',
			)} - $${formatNumber(stateAfterResponse.order_amount_max?.toString() || '0')}`;
		}

		return amount;
	};

	const handleAccept = () => {
		setResponse({
			pl_custom_url: null,
			pl_custom_qr: null,
		});
		handleCloseModal();
	};

	const initValue = {
		value: currentValue,
		label: 'Ahora',
		show: true,
	};
	const [hoursMinutesStart, setHoursMinutesStart] = useState([initValue, ...HoursMinutes]);
	const [hoursMinutesEnd, setHoursMinutesEnd] = useState([...HoursMinutes]);

	const [formattedAmount, setFormattedAmount] = useState('');
	const [formattedMaxAmount, setFormattedMaxAmount] = useState('');

	function formatNumber(value: string): string {
		const parsedValue = Number(value.replace(/[^0-9]/g, ''));
		return parsedValue
			.toLocaleString('es-CL', {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			})
			.replace(',', '.');
	}

	return (
		<ModalLinks
			onClose={handleCloseModal}
			show={show}
			title={response.pl_custom_qr ? 'Enlace de cobro' : 'Crear enlace de cobro'}
			type={response.pl_custom_qr ? 'medium' : 'large'}
		>
			{state.loading ? (
				<Spinner message="Creando enlace de cobro" />
			) : (
				<>
					<ModalContainer>
						{!response.pl_custom_url && (
							<ContainerForm>
								<DataLink>
									<HeaderStep>
										<CircleStepOne /> <TitleStep>Selecciona el tipo de cobro</TitleStep>
									</HeaderStep>
									<BodyStep>
										<PLCardLink
											onClick={() => {
												setPlCardSelected('UNIQUE');
												setState({
													...state,
													payment_type: 'UNIQUE',
													num_payments: 1,
												});
											}}
											payType={'unique'}
											selected={plCardSelected === 'UNIQUE'}
										/>
										<PLCardLink
											onClick={() => {
												setPlCardSelected('RECURRENT');
												setState({
													...state,
													payment_type: 'RECURRENT',
												});
											}}
											payType={'recurrent'}
											selected={plCardSelected === 'RECURRENT'}
										/>
									</BodyStep>
									<DividerLink />
									<ContainerAmount>
										<ContainerSelectNative>
											<SelectNavite
												onChange={(e) => {
													const type = e.target.value as AmountType;
													if (type === 'FIXED') {
														setState({
															...state,
															amount_type: type,
															order_amount: state.order_amount_min,
															order_amount_min: null,
															order_amount_max: null,
														});
													} else {
														setState({
															...state,
															amount_type: type,
															order_amount: null,
															order_amount_min: state.order_amount,
															order_amount_max: null,
														});
													}

													setTypeAmoun(type);
												}}
											>
												{optionsMock.map((opt, idx) => (
													<option key={`opt-${idx}-${opt.label}`} value={opt.value}>
														{opt.label}
													</option>
												))}
											</SelectNavite>
										</ContainerSelectNative>

										<Input
											hasFocus
											height={48}
											label={`${typeAmount === 'VARIABLE' ? 'Monto mínimo' : 'Monto'}`}
											labelFocus
											fontSize="16px"
											style={{ background: '#FCFCFC' }}
											min={0}
											maxLength={10}
											name={typeAmount === 'VARIABLE' ? 'order_amount_min' : 'order_amount'}
											value={formattedAmount} // Muestra el valor formateado
											onChange={(e) => {
												const value = e.target.value;
												const formattedValue = formatNumber(value); // Formatea el valor
												setFormattedAmount(formattedValue);
											}}
											onKeyDown={(e) => handleKeydown(e)}
											step={1}
											onBlur={(e) => {
												const { name, value } = e.target;
												const parsedValue = Number(value.replace(/[^0-9]/g, '')); // Elimina los separadores de miles
												if (
													typeAmount === 'VARIABLE' &&
													state.order_amount_max &&
													parsedValue > state.order_amount_max
												) {
													handleShowAlert({
														message: 'El monto mínimo no puede ser mayor al monto máximo',
														type: 'error',
													});
													return;
												}

												let valueAmounts;
												if (plCardSelected === 'UNIQUE') {
													valueAmounts = parsedValue;
												} else {
													valueAmounts = value;
												}

												setState({ ...state, [name]: valueAmounts });
												validateFields(state);
											}}
											placeholder="$5.000"
											prefix="$"
											type="text" // Cambia el tipo a "text" para permitir el formateado
											variant="large"
										/>
										{typeAmount === 'VARIABLE' && (
											<Input
												hasFocus
												height={48}
												label="Monto máximo"
												labelFocus
												fontSize="16px"
												step={1}
												style={{ background: '#FCFCFC' }}
												min={0}
												maxLength={10}
												name="order_amount_max"
												value={formattedMaxAmount} // Muestra el valor formateado
												onChange={(e) => {
													const value = e.target.value;
													const formattedValue = formatNumber(value); // Formatea el valor
													setFormattedMaxAmount(formattedValue);
												}}
												onKeyDown={(e) => handleKeydown(e)}
												onBlur={(e) => {
													const { name, value } = e.target;
													const parsedValue = Number(value.replace(/[^0-9]/g, '')); // Elimina los separadores de miles
													if (
														typeAmount === 'VARIABLE' &&
														state.order_amount_min &&
														parsedValue < state.order_amount_min
													) {
														validateFields(state);
														handleShowAlert({
															message: 'El monto máximo no puede ser menor al monto mínimo',
															type: 'error',
														});
														return;
													}

													setState({ ...state, [name]: parsedValue }); // Guarda el valor sin formato
												}}
												placeholder="$1.000.000"
												prefix="$"
												type="text" // Cambia el tipo a "text" para permitir el formateado
												variant="large"
											/>
										)}
									</ContainerAmount>
									{plCardSelected === 'RECURRENT' && (
										<ContainerTextAndName>
											<WarningText>
												Los montos máximos a transferir están sujetos a las restricciones de cada
												banco.
											</WarningText>
											<Input
												hasFocus
												height={48}
												label="Cantidad de pagos aceptados"
												labelFocus
												fontSize="16px"
												min={0}
												name="num_payments"
												placeholder="Sin limite"
												step={1}
												style={{ background: '#FCFCFC' }}
												type="number"
												variant="large"
												onKeyDown={(e) => handleKeydown(e)}
												onChange={(e) => {
													handleChangeInput(e);
												}}
											/>
										</ContainerTextAndName>
									)}
									<DividerLink />
									<ContainerTextAndName>
										<Input
											hasFocus
											height={48}
											label="Nombre del enlace de cobro o ID de referencia"
											labelFocus
											fontSize="16px"
											name="name"
											onChange={(e) => handleChangeInput(e)}
											onKeyDown={(e) => handleKeydown(e)}
											placeholder="Ej. nombre del cliente, referencia del cobro."
											style={{ background: '#FCFCFC' }}
											variant="large"
										/>
									</ContainerTextAndName>
									<DividerLink />
									<ContainerTextAndName>
										<Disclaimer>Personaliza la fecha del enlace de cobro</Disclaimer>
										<ContainerDates>
											<InputContainer>
												<Input
													hasFocus
													labelFocus
													height={48}
													style={{ background: '#FCFCFC' }}
													fontSize="16px"
													label="Fecha de inicio"
													value={range.from.toLocaleDateString()}
													variant="large"
													rightIcon={<CalendarIcon />}
													disabled={false}
													rightIconClick={() => {
														setShowCalendar({ ...showCalendar, from: !showCalendar.from });
													}}
													onClick={() => {
														setShowCalendar({ ...showCalendar, from: !showCalendar.from });
													}}
													onChange={() =>
														setShowCalendar({ ...showCalendar, from: !showCalendar.from })
													}
												/>
												{showCalendar.from && (
													<ShowDatePickerContainer y="bottom" x="left">
														<ReactDatePicker
															inline
															locale="es"
															selected={range.from}
															minDate={new Date()}
															maxDate={new Date(new Date().setDate(new Date().getDate() + 730))}
															onChange={(date) => onChangeDate(date as Date, 'from')}
															renderCustomHeader={(params) => (
																<>
																	<HeaderMonthYearSelected>
																		<ChangeMonthContainer
																			onClick={() => {
																				if (!params.prevMonthButtonDisabled) params.decreaseMonth();
																			}}
																			allowClick={!params.prevMonthButtonDisabled}
																		>
																			<LeftAngleIcon />
																		</ChangeMonthContainer>

																		<CurrentMonthYear>
																			{getMonthAndYearFormat(params.date)}
																		</CurrentMonthYear>

																		<ChangeMonthContainer
																			onClick={() => {
																				if (!params.nextMonthButtonDisabled) params.increaseMonth();
																			}}
																			allowClick={!params.nextMonthButtonDisabled}
																		>
																			<RightAngleIcon />
																		</ChangeMonthContainer>
																	</HeaderMonthYearSelected>
																</>
															)}
														/>
													</ShowDatePickerContainer>
												)}
											</InputContainer>
											<ContainerSelectNative width="100%">
												<SelectNavite
													disabled={false}
													width="100%"
													onChange={(e) => {
														const selectedTime = e.target.value as string;

														if (selectedTime === currentValue) {
															const newDate = new Date();
															setState({
																...state,
																init_expired_at: newDate,
																end_expired_at: newDate,
															});
														} else {
															const [hours, minutes] = selectedTime.split(':');
															const newDate = new Date(range.from);
															newDate.setHours(parseInt(hours));
															newDate.setMinutes(parseInt(minutes));
															setState({
																...state,
																init_expired_at: newDate,
																end_expired_at: newDate,
															});
														}
													}}
												>
													{hoursMinutesStart.map((hour, idx) => (
														<option key={`opt-start-${idx}-${hour.value}`} value={hour.value}>
															{hour.label}
														</option>
													))}
												</SelectNavite>
											</ContainerSelectNative>
										</ContainerDates>
										<Checkbox
											label="Con fecha de vencimiento"
											name="withExpirationDate"
											checked={withExpirationDate}
											onChange={() => {
												handleWithExpirationDate();
											}}
											fontSize="12px"
										/>

										{withExpirationDate && (
											<ContainerDates>
												<InputContainer>
													<Input
														style={{ background: '#FCFCFC' }}
														hasFocus
														labelFocus
														height={48}
														label="Fecha de vencimiento"
														fontSize="16px"
														value={range.to.toLocaleDateString()}
														variant="large"
														rightIcon={<CalendarIcon />}
														disabled={false}
														rightIconClick={() => {
															setShowCalendar({ ...showCalendar, to: !showCalendar.to });
														}}
														onClick={() => {
															setShowCalendar({ ...showCalendar, to: !showCalendar.to });
														}}
														onChange={() =>
															setShowCalendar({ ...showCalendar, to: !showCalendar.to })
														}
													/>
													{showCalendar.to && (
														<ShowDatePickerContainer y="bottom" x="left">
															<ReactDatePicker
																inline
																locale="es"
																selected={range.to}
																minDate={new Date()}
																maxDate={new Date(new Date().setDate(new Date().getDate() + 730))}
																onChange={(date) => onChangeDate(date as Date, 'to')}
																renderCustomHeader={(params) => (
																	<>
																		<HeaderMonthYearSelected>
																			<ChangeMonthContainer
																				onClick={() => {
																					if (!params.prevMonthButtonDisabled)
																						params.decreaseMonth();
																				}}
																				allowClick={!params.prevMonthButtonDisabled}
																			>
																				<LeftAngleIcon />
																			</ChangeMonthContainer>

																			<CurrentMonthYear>
																				{getMonthAndYearFormat(params.date)}
																			</CurrentMonthYear>

																			<ChangeMonthContainer
																				onClick={() => {
																					if (!params.nextMonthButtonDisabled)
																						params.increaseMonth();
																				}}
																				allowClick={!params.nextMonthButtonDisabled}
																			>
																				<RightAngleIcon />
																			</ChangeMonthContainer>
																		</HeaderMonthYearSelected>
																	</>
																)}
															/>
														</ShowDatePickerContainer>
													)}
												</InputContainer>
												<ContainerSelectNative width="100%">
													<SelectNavite
														disabled={false}
														width="100%"
														onChange={(e) => {
															const selectedTime = e.target.value as string;
															const [hours, minutes] = selectedTime.split(':');

															const newDate = new Date(range.to);
															newDate.setHours(parseInt(hours));
															newDate.setMinutes(parseInt(minutes));

															// Compare times only when dates are the same
															if (
																range.from.getDate() === range.to.getDate() &&
																range.from.getMonth() === range.to.getMonth() &&
																range.from.getFullYear() === range.to.getFullYear() &&
																newDate <= state.init_expired_at
															) {
																handleShowAlert({
																	message:
																		'La hora de término debe ser posterior a la hora de inicio',
																	type: 'error',
																});
																return;
															}

															setRange({
																from: state.init_expired_at,
																to: newDate,
															});

															setState({
																...state,
																end_expired_at: newDate,
															});
														}}
														defaultValue={hoursMinutesEnd[24].value}
													>
														{hoursMinutesEnd.map((hour, idx) => (
															<option
																key={`opt-end-${idx}-${hour.value}`}
																value={hour.value}
																selected={hoursMinutesEnd[24].value === hour.value}
															>
																{hour.label}
															</option>
														))}
													</SelectNavite>
												</ContainerSelectNative>
											</ContainerDates>
										)}
									</ContainerTextAndName>
								</DataLink>
								<DividerTabs />
								<CustomDataLink>
									<HeaderContainer>
										<HeaderStep>
											<CircleStepTwo />{' '}
											<TitleStep>Personaliza la información del cliente</TitleStep>
										</HeaderStep>
										<ParagraphGlosa>
											Estos son los datos que el destinatario del enlace deberá rellenar para
											acceder a método de pago.
										</ParagraphGlosa>
									</HeaderContainer>
									<CardForm>
										<CardContainer
											disabled={dinamicFields.find((f) => f.key === 'rut') ? false : true}
										>
											<CardField>
												<span>RUT</span>
												<Switch
													id="rut"
													name="rut"
													onChange={() => handleToggleCustomField('rut')}
												/>
											</CardField>
											<Checkbox
												label="Obligatorio"
												name={'required-rut'}
												disabled={!dinamicFields.find((f) => f.key === 'rut')}
												checked={checkedRequiredFields['rut'] || false}
												onChange={() => handleToggleRequired('rut')}
												gap="4px"
												fontSize="12px"
											/>
										</CardContainer>
										<DividerForm />
										<CardContainer
											disabled={dinamicFields.find((f) => f.key === 'names') ? false : true}
										>
											<CardField>
												<span>Nombre y apellidos</span>
												<Switch
													id="names"
													name="names"
													onChange={() => handleToggleCustomField('names')}
												/>
											</CardField>
											<Checkbox
												onChange={() => handleToggleRequired('names')}
												label="Obligatorio"
												disabled={!dinamicFields.find((f) => f.key === 'names')}
												checked={checkedRequiredFields['names'] || false}
												name={'required-names'}
												gap="4px"
												fontSize="12px"
											/>
										</CardContainer>
										<DividerForm />
										<CardContainer
											disabled={dinamicFields.find((f) => f.key === 'email') ? false : true}
										>
											<CardField>
												<span>Correo electrónico</span>
												<Switch
													id="email"
													name="email"
													onChange={() => handleToggleCustomField('email')}
												/>
											</CardField>
											<Checkbox
												label="Obligatorio"
												name={'required-email'}
												disabled={!dinamicFields.find((f) => f.key === 'email')}
												checked={checkedRequiredFields['email'] || false}
												onChange={() => handleToggleRequired('email')}
												gap="4px"
												fontSize="12px"
											/>
										</CardContainer>
									</CardForm>
								</CustomDataLink>
							</ContainerForm>
						)}
						{response.pl_custom_qr && (
							<QRTabPL
								id="qr-pl"
								amount={amountsQR}
								merchantName={merchantSelected?.name || ''}
								paymentLinkUrl={response.pl_custom_url || ''}
								paymentLinkUrlQR={response.pl_custom_qr || ''}
								paymentLinkName={state.name || ''}
							/>
						)}
					</ModalContainer>
					{!response.pl_custom_qr && (
						<ModalFooter>
							<ETPayButton
								color="secondary"
								onClick={() => handleAddPL()}
								title="Generar enlace"
								variant="default"
								disabled={!validateFields(state)}
							/>
						</ModalFooter>
					)}

					{response.pl_custom_qr && (
						<ModalFooterQr>
							<ETPayButton
								color="secondary"
								onClick={() => handleAccept()}
								title="Entendido"
								variant="default"
								style={{
									width: '60%',
								}}
							/>
						</ModalFooterQr>
					)}
				</>
			)}
		</ModalLinks>
	);
};

export { NewLinkPL };
